import React, { useState, useEffect } from 'react';
import { useAuth } from '../../lib/auth-context';
import { posApi, roomsApi } from '../../lib/api';
import { format } from 'date-fns';
import {
  UtensilsCrossed,
  Plus,
  DollarSign,
  Receipt,
  Users,
  CreditCard,
  Banknote,
  BedDouble,
  Check,
  X,
  MoreVertical,
  Search,
  RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';

export default function POSPage() {
  const { currentProperty } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [checks, setChecks] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [menuCategories, setMenuCategories] = useState([]);
  const [selectedCheck, setSelectedCheck] = useState(null);
  
  // Dialogs
  const [showNewCheckDialog, setShowNewCheckDialog] = useState(false);
  const [showAddItemDialog, setShowAddItemDialog] = useState(false);
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [showOutletDialog, setShowOutletDialog] = useState(false);
  const [showMenuItemDialog, setShowMenuItemDialog] = useState(false);

  // Form states
  const [checkForm, setCheckForm] = useState({
    table_number: '',
    covers: 1,
  });
  const [itemForm, setItemForm] = useState({
    item_name: '',
    quantity: 1,
    unit_price: 0,
    tax_rate: 15,
  });
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [outletForm, setOutletForm] = useState({
    code: '',
    name: '',
    outlet_type: 'RESTAURANT',
  });
  const [menuItemForm, setMenuItemForm] = useState({
    code: '',
    name: '',
    unit_price: 0,
    tax_rate: 15,
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [dashboardRes, outletsRes, checksRes] = await Promise.all([
          posApi.getDashboard(currentProperty.id),
          posApi.listOutlets(currentProperty.id),
          posApi.listChecks(currentProperty.id),
        ]);
        setDashboard(dashboardRes.data);
        setOutlets(outletsRes.data || []);
        setChecks(checksRes.data || []);
        
        if (outletsRes.data?.length > 0 && !selectedOutlet) {
          setSelectedOutlet(outletsRes.data[0].id);
        }
      } catch (error) {
        console.error('Failed to load POS data:', error);
        toast.error('Failed to load POS data');
      } finally {
        setLoading(false);
      }
    };

    if (currentProperty?.id) {
      loadData();
    }
  }, [currentProperty, selectedOutlet]);

  useEffect(() => {
    const loadOutletData = async (outletId) => {
      try {
        const [menuRes, categoriesRes] = await Promise.all([
          posApi.getMenu(outletId),
          posApi.getMenuCategories(outletId),
        ]);
        setMenuItems(menuRes.data || []);
        setMenuCategories(categoriesRes.data || []);
      } catch (error) {
        console.error('Failed to load outlet data:', error);
      }
    };

    if (selectedOutlet) {
      loadOutletData(selectedOutlet);
    }
  }, [selectedOutlet]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [dashboardRes, outletsRes, checksRes] = await Promise.all([
        posApi.getDashboard(currentProperty.id),
        posApi.listOutlets(currentProperty.id),
        posApi.listChecks(currentProperty.id),
      ]);
      setDashboard(dashboardRes.data);
      setOutlets(outletsRes.data || []);
      setChecks(checksRes.data || []);
      
      if (outletsRes.data?.length > 0 && !selectedOutlet) {
        setSelectedOutlet(outletsRes.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load POS data:', error);
      toast.error('Failed to load POS data');
    } finally {
      setLoading(false);
    }
  };

  const loadOutletData = async (outletId) => {
    try {
      const [menuRes, categoriesRes] = await Promise.all([
        posApi.getMenu(outletId),
        posApi.getMenuCategories(outletId),
      ]);
      setMenuItems(menuRes.data || []);
      setMenuCategories(categoriesRes.data || []);
    } catch (error) {
      console.error('Failed to load outlet data:', error);
    }
  };

  const handleCreateCheck = async () => {
    try {
      if (!selectedOutlet) {
        toast.error('Please select an outlet first');
        return;
      }
      
      const result = await posApi.createCheck(currentProperty.id, {
        outlet_id: selectedOutlet,
        table_number: checkForm.table_number,
        covers: parseInt(checkForm.covers) || 1,
      });
      
      setChecks([result.data, ...checks]);
      setSelectedCheck(result.data);
      setShowNewCheckDialog(false);
      setCheckForm({ table_number: '', covers: 1 });
      toast.success('Check opened successfully');
    } catch (error) {
      toast.error('Failed to create check');
    }
  };

  const handleAddItem = async () => {
    try {
      if (!selectedCheck) return;
      
      await posApi.addCheckItem(selectedCheck.id, {
        item_name: itemForm.item_name,
        quantity: parseInt(itemForm.quantity) || 1,
        unit_price: parseFloat(itemForm.unit_price) || 0,
        tax_rate: parseFloat(itemForm.tax_rate) || 0,
      });
      
      // Reload check
      const checkRes = await posApi.getCheck(selectedCheck.id);
      setSelectedCheck(checkRes.data);
      
      // Reload checks list
      const checksRes = await posApi.listChecks(currentProperty.id);
      setChecks(checksRes.data || []);
      
      setShowAddItemDialog(false);
      setItemForm({ item_name: '', quantity: 1, unit_price: 0, tax_rate: 15 });
      toast.success('Item added to check');
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  const handlePayCheck = async () => {
    try {
      if (!selectedCheck) return;
      
      await posApi.payCheck(selectedCheck.id, currentProperty.id, {
        payment_method: paymentMethod,
      });
      
      // Reload data
      await loadData();
      setSelectedCheck(null);
      setShowPayDialog(false);
      toast.success('Payment processed successfully');
    } catch (error) {
      toast.error('Failed to process payment');
    }
  };

  const handleVoidCheck = async (checkId) => {
    try {
      await posApi.voidCheck(checkId, 'Voided by manager');
      await loadData();
      if (selectedCheck?.id === checkId) {
        setSelectedCheck(null);
      }
      toast.success('Check voided');
    } catch (error) {
      toast.error('Failed to void check');
    }
  };

  const handleCreateOutlet = async () => {
    try {
      await posApi.createOutlet(currentProperty.id, outletForm);
      await loadData();
      setShowOutletDialog(false);
      setOutletForm({ code: '', name: '', outlet_type: 'RESTAURANT' });
      toast.success('Outlet created successfully');
    } catch (error) {
      toast.error('Failed to create outlet');
    }
  };

  const handleCreateMenuItem = async () => {
    try {
      if (!selectedOutlet) return;
      
      await posApi.createMenuItem(selectedOutlet, menuItemForm);
      await loadOutletData(selectedOutlet);
      setShowMenuItemDialog(false);
      setMenuItemForm({ code: '', name: '', unit_price: 0, tax_rate: 15 });
      toast.success('Menu item created');
    } catch (error) {
      toast.error('Failed to create menu item');
    }
  };

  const selectMenuItem = (item) => {
    setItemForm({
      item_name: item.name,
      quantity: 1,
      unit_price: item.unit_price,
      tax_rate: item.tax_rate || 15,
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      OPEN: 'bg-success/10 text-success',
      PAID: 'bg-primary/10 text-primary',
      VOID: 'bg-destructive/10 text-destructive',
    };
    return <Badge className={styles[status] || 'bg-muted'}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="pos-page-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pos-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Point of Sale</h1>
          <p className="text-muted-foreground">
            {format(new Date(), 'EEEE, MMMM d, yyyy')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowOutletDialog(true)} variant="outline" data-testid="new-outlet-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Outlet
          </Button>
          <Button onClick={() => setShowNewCheckDialog(true)} data-testid="new-check-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Check
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card data-testid="stat-total-sales">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-success/10">
                  <DollarSign className="h-6 w-6 text-success" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Sales</p>
                  <p className="text-2xl font-bold">${dashboard.total_sales?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-open-checks">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-warning/10">
                  <Receipt className="h-6 w-6 text-warning" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Open Checks</p>
                  <p className="text-2xl font-bold">{dashboard.open_checks || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-paid-checks">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-primary/10">
                  <Check className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Paid Checks</p>
                  <p className="text-2xl font-bold">{dashboard.paid_checks || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-covers">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-chart-2/10">
                  <Users className="h-6 w-6 text-chart-2" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Covers</p>
                  <p className="text-2xl font-bold">{dashboard.total_covers || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Checks List */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex-row items-center justify-between">
            <div>
              <CardTitle>Active Checks</CardTitle>
              <CardDescription>Open and recent checks for today</CardDescription>
            </div>
            <Select value={selectedOutlet || ''} onValueChange={setSelectedOutlet}>
              <SelectTrigger className="w-48" data-testid="outlet-select">
                <SelectValue placeholder="Select outlet" />
              </SelectTrigger>
              <SelectContent>
                {outlets.map(outlet => (
                  <SelectItem key={outlet.id} value={outlet.id}>
                    {outlet.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            {checks.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No checks for today</p>
                <Button 
                  variant="link" 
                  onClick={() => setShowNewCheckDialog(true)}
                  className="mt-2"
                >
                  Open a new check
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Check #</TableHead>
                    <TableHead>Table</TableHead>
                    <TableHead>Covers</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {checks.map(check => (
                    <TableRow 
                      key={check.id} 
                      className={selectedCheck?.id === check.id ? 'bg-muted' : ''}
                      onClick={() => check.status === 'OPEN' && setSelectedCheck(check)}
                      style={{ cursor: check.status === 'OPEN' ? 'pointer' : 'default' }}
                      data-testid={`check-row-${check.id}`}
                    >
                      <TableCell className="font-medium">{check.check_number}</TableCell>
                      <TableCell>{check.table_number || '-'}</TableCell>
                      <TableCell>{check.covers}</TableCell>
                      <TableCell>${check.total_amount?.toFixed(2) || '0.00'}</TableCell>
                      <TableCell>{getStatusBadge(check.status)}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {check.status === 'OPEN' && (
                              <>
                                <DropdownMenuItem onClick={() => {
                                  setSelectedCheck(check);
                                  setShowAddItemDialog(true);
                                }}>
                                  Add Item
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => {
                                  setSelectedCheck(check);
                                  setShowPayDialog(true);
                                }}>
                                  Pay
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleVoidCheck(check.id)}
                                  className="text-destructive"
                                >
                                  Void
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Menu / Check Detail Panel */}
        <Card>
          <CardHeader>
            <CardTitle>
              {selectedCheck ? `Check #${selectedCheck.check_number}` : 'Quick Menu'}
            </CardTitle>
            <CardDescription>
              {selectedCheck ? `Table: ${selectedCheck.table_number || 'N/A'} | Covers: ${selectedCheck.covers}` : 'Select items to add'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {selectedCheck ? (
              <div className="space-y-4">
                {/* Check Items */}
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {selectedCheck.items?.length > 0 ? (
                    selectedCheck.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 border rounded">
                        <div>
                          <p className="font-medium">{item.item_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {item.quantity} x ${item.unit_price?.toFixed(2)}
                          </p>
                        </div>
                        <p className="font-medium">${item.total_amount?.toFixed(2)}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-center py-4 text-muted-foreground">No items yet</p>
                  )}
                </div>
                
                {/* Totals */}
                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>${selectedCheck.subtotal?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tax</span>
                    <span>${selectedCheck.tax_amount?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span>${selectedCheck.total_amount?.toFixed(2) || '0.00'}</span>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex gap-2">
                  <Button 
                    className="flex-1" 
                    variant="outline"
                    onClick={() => setShowAddItemDialog(true)}
                    data-testid="add-item-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Item
                  </Button>
                  <Button 
                    className="flex-1"
                    onClick={() => setShowPayDialog(true)}
                    data-testid="pay-btn"
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    Pay
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Menu Items</span>
                  <Button size="sm" variant="ghost" onClick={() => setShowMenuItemDialog(true)}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {menuItems.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">
                    No menu items yet
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {menuItems.slice(0, 8).map(item => (
                      <Button
                        key={item.id}
                        variant="outline"
                        className="h-auto py-3 flex-col"
                        onClick={() => {
                          if (selectedCheck) {
                            selectMenuItem(item);
                            setShowAddItemDialog(true);
                          }
                        }}
                        disabled={!selectedCheck}
                      >
                        <span className="font-medium text-xs">{item.name}</span>
                        <span className="text-muted-foreground text-xs">${item.unit_price?.toFixed(2)}</span>
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* New Check Dialog */}
      <Dialog open={showNewCheckDialog} onOpenChange={setShowNewCheckDialog}>
        <DialogContent data-testid="new-check-dialog">
          <DialogHeader>
            <DialogTitle>Open New Check</DialogTitle>
            <DialogDescription>Create a new POS check</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Table Number</Label>
              <Input
                value={checkForm.table_number}
                onChange={(e) => setCheckForm({ ...checkForm, table_number: e.target.value })}
                placeholder="e.g., T1, A5"
                data-testid="table-number-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Covers</Label>
              <Input
                type="number"
                min="1"
                value={checkForm.covers}
                onChange={(e) => setCheckForm({ ...checkForm, covers: e.target.value })}
                data-testid="covers-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCheckDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateCheck} data-testid="create-check-btn">
              Open Check
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Item Dialog */}
      <Dialog open={showAddItemDialog} onOpenChange={setShowAddItemDialog}>
        <DialogContent data-testid="add-item-dialog">
          <DialogHeader>
            <DialogTitle>Add Item</DialogTitle>
            <DialogDescription>Add item to check</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Item Name</Label>
              <Input
                value={itemForm.item_name}
                onChange={(e) => setItemForm({ ...itemForm, item_name: e.target.value })}
                placeholder="Item name"
                data-testid="item-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input
                  type="number"
                  min="1"
                  value={itemForm.quantity}
                  onChange={(e) => setItemForm({ ...itemForm, quantity: e.target.value })}
                  data-testid="item-quantity-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Unit Price</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={itemForm.unit_price}
                  onChange={(e) => setItemForm({ ...itemForm, unit_price: e.target.value })}
                  data-testid="item-price-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddItemDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddItem} data-testid="confirm-add-item-btn">
              Add Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay Dialog */}
      <Dialog open={showPayDialog} onOpenChange={setShowPayDialog}>
        <DialogContent data-testid="pay-dialog">
          <DialogHeader>
            <DialogTitle>Process Payment</DialogTitle>
            <DialogDescription>
              Total: ${selectedCheck?.total_amount?.toFixed(2) || '0.00'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Label>Payment Method</Label>
            <div className="grid grid-cols-3 gap-4">
              <Button
                variant={paymentMethod === 'CASH' ? 'default' : 'outline'}
                onClick={() => setPaymentMethod('CASH')}
                className="flex-col h-20"
                data-testid="payment-cash-btn"
              >
                <Banknote className="h-6 w-6 mb-1" />
                Cash
              </Button>
              <Button
                variant={paymentMethod === 'CREDIT_CARD' ? 'default' : 'outline'}
                onClick={() => setPaymentMethod('CREDIT_CARD')}
                className="flex-col h-20"
                data-testid="payment-card-btn"
              >
                <CreditCard className="h-6 w-6 mb-1" />
                Card
              </Button>
              <Button
                variant={paymentMethod === 'ROOM_CHARGE' ? 'default' : 'outline'}
                onClick={() => setPaymentMethod('ROOM_CHARGE')}
                className="flex-col h-20"
                data-testid="payment-room-btn"
              >
                <BedDouble className="h-6 w-6 mb-1" />
                Room
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handlePayCheck} data-testid="confirm-payment-btn">
              Process Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Outlet Dialog */}
      <Dialog open={showOutletDialog} onOpenChange={setShowOutletDialog}>
        <DialogContent data-testid="new-outlet-dialog">
          <DialogHeader>
            <DialogTitle>Create Outlet</DialogTitle>
            <DialogDescription>Add a new POS outlet</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Code</Label>
              <Input
                value={outletForm.code}
                onChange={(e) => setOutletForm({ ...outletForm, code: e.target.value })}
                placeholder="e.g., REST01"
                data-testid="outlet-code-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={outletForm.name}
                onChange={(e) => setOutletForm({ ...outletForm, name: e.target.value })}
                placeholder="e.g., Main Restaurant"
                data-testid="outlet-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select 
                value={outletForm.outlet_type} 
                onValueChange={(v) => setOutletForm({ ...outletForm, outlet_type: v })}
              >
                <SelectTrigger data-testid="outlet-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="RESTAURANT">Restaurant</SelectItem>
                  <SelectItem value="BAR">Bar</SelectItem>
                  <SelectItem value="ROOM_SERVICE">Room Service</SelectItem>
                  <SelectItem value="SPA">Spa</SelectItem>
                  <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOutletDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateOutlet} data-testid="create-outlet-btn">
              Create Outlet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Menu Item Dialog */}
      <Dialog open={showMenuItemDialog} onOpenChange={setShowMenuItemDialog}>
        <DialogContent data-testid="new-menu-item-dialog">
          <DialogHeader>
            <DialogTitle>Add Menu Item</DialogTitle>
            <DialogDescription>Add item to menu</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Code</Label>
              <Input
                value={menuItemForm.code}
                onChange={(e) => setMenuItemForm({ ...menuItemForm, code: e.target.value })}
                placeholder="e.g., BURGER01"
                data-testid="menu-item-code-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={menuItemForm.name}
                onChange={(e) => setMenuItemForm({ ...menuItemForm, name: e.target.value })}
                placeholder="e.g., Classic Burger"
                data-testid="menu-item-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Price</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={menuItemForm.unit_price}
                  onChange={(e) => setMenuItemForm({ ...menuItemForm, unit_price: e.target.value })}
                  data-testid="menu-item-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Tax Rate (%)</Label>
                <Input
                  type="number"
                  value={menuItemForm.tax_rate}
                  onChange={(e) => setMenuItemForm({ ...menuItemForm, tax_rate: e.target.value })}
                  data-testid="menu-item-tax-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMenuItemDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateMenuItem} data-testid="create-menu-item-btn">
              Add Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
